import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import FilterSidebar, { type FilterState } from './components/FilterSidebar';
import MovieCard from '@/components/feature/MovieCard';
import { GENRES, MEDIA_LIST, REGIONS, YEARS, type MediaType } from '@/mocks/media';

type SortKey = 'updated' | 'views' | 'rating';

const SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: 'updated', label: '按更新时间' },
  { key: 'rating', label: '按评分' },
  { key: 'views', label: '按热度' },
];

const PAGE_SIZE = 12;

const EMPTY_FILTER: FilterState = { genres: [], regions: [], years: [] };

export default function CategoryPage() {
  const { type } = useParams<{ type: string }>();
  const mediaType: MediaType = type === 'tv' ? 'tv' : 'movie';

  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTER);
  const [sort, setSort] = useState<SortKey>('updated');
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  useEffect(() => {
    setFilters(EMPTY_FILTER);
    setSort('updated');
    setVisible(PAGE_SIZE);
  }, [mediaType]);

  const baseList = useMemo(() => MEDIA_LIST.filter((item) => item.type === mediaType), [mediaType]);

  const filtered = useMemo(() => {
    const result = baseList.filter((item) => {
      const genreOk = filters.genres.length === 0 || item.genres.some((g) => filters.genres.includes(g));
      const regionOk = filters.regions.length === 0 || filters.regions.includes(item.region);
      const yearOk = filters.years.length === 0 || filters.years.includes(item.year);
      return genreOk && regionOk && yearOk;
    });
    return result.sort((a, b) => {
      if (sort === 'rating') return b.rating - a.rating;
      if (sort === 'views') return b.views - a.views;
      return b.updatedAt.localeCompare(a.updatedAt);
    });
  }, [baseList, filters, sort]);

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [filters, sort]);

  const hasActive = filters.genres.length > 0 || filters.regions.length > 0 || filters.years.length > 0;

  const toggle = (group: keyof FilterState, value: string | number) => {
    setFilters((prev) => {
      const list = prev[group] as (string | number)[];
      const exists = list.includes(value);
      const next = exists ? list.filter((v) => v !== value) : [...list, value];
      return { ...prev, [group]: next } as FilterState;
    });
  };

  const reset = () => setFilters(EMPTY_FILTER);

  const title = mediaType === 'movie' ? '电影频道' : '电视剧频道';
  const subtitle =
    mediaType === 'movie'
      ? '最新高清电影推荐，动作、喜剧、科幻、悬疑全类型覆盖'
      : '热播剧集持续更新，都市、古装、悬疑、爱情一网打尽';

  const shown = filtered.slice(0, visible);
  const sidebar = (
    <FilterSidebar
      genres={GENRES}
      regions={REGIONS}
      years={YEARS}
      state={filters}
      onToggle={toggle}
      onReset={reset}
      hasActive={hasActive}
    />
  );

  return (
    <div className="w-full">
      <div className="w-full bg-primary-950 text-background-50">
        <div className="px-4 md:px-8 py-8 md:py-12">
          <nav className="flex items-center gap-2 text-xs text-background-300 mb-4">
            <Link to="/" className="hover:text-accent-400 transition-colors cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-background-100">{title}</span>
          </nav>
          <h1 className="font-heading font-bold text-2xl md:text-4xl">{title}</h1>
          <p className="mt-2 text-sm md:text-base text-background-200">{subtitle}</p>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-8 md:py-10">
        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6 lg:gap-8">
          <aside className="hidden lg:block">
            <div className="lg:sticky lg:top-24">{sidebar}</div>
          </aside>

          <div>
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <button
                type="button"
                onClick={() => setMobileFilterOpen(true)}
                className="lg:hidden inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-700 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-filter-3-line"></i> 筛选
                {hasActive && <span className="w-1.5 h-1.5 rounded-full bg-accent-500"></span>}
              </button>

              <span className="text-sm text-foreground-500">
                共 <strong className="text-primary-600">{filtered.length}</strong> 部作品
              </span>

              <div className="ml-auto flex items-center gap-1 rounded-full bg-background-100 border border-background-200 p-1">
                {SORT_OPTIONS.map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setSort(opt.key)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      sort === opt.key ? 'bg-primary-500 text-background-50' : 'text-foreground-600 hover:text-primary-600'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {shown.length === 0 ? (
              <div className="py-20 text-center rounded-lg border border-dashed border-background-300 bg-background-50">
                <span className="mx-auto w-14 h-14 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                  <i className="ri-film-line text-2xl"></i>
                </span>
                <p className="text-foreground-700 font-medium">未找到符合条件的影视作品</p>
                <p className="text-sm text-foreground-500 mt-1">试试调整或清空筛选条件</p>
                <button
                  type="button"
                  onClick={reset}
                  className="mt-5 inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-refresh-line"></i> 清空筛选
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
                  {shown.map((item) => (
                    <MovieCard key={item.id} item={item} />
                  ))}
                </div>
                {visible < filtered.length && (
                  <div className="mt-8 flex justify-center">
                    <button
                      type="button"
                      onClick={() => setVisible((v) => v + PAGE_SIZE)}
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-primary-200 bg-primary-50 text-primary-700 text-sm font-medium hover:bg-primary-100 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-add-line"></i> 加载更多
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {mobileFilterOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div className="absolute inset-0 bg-primary-950/50" onClick={() => setMobileFilterOpen(false)}></div>
          <div className="absolute bottom-0 inset-x-0 max-h-[85vh] overflow-y-auto rounded-t-2xl bg-background-50 p-5 animate-fade-up">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-foreground-950">筛选条件</h3>
              <button type="button" aria-label="关闭" onClick={() => setMobileFilterOpen(false)} className="w-9 h-9 flex items-center justify-center rounded-md bg-background-100 text-foreground-700 cursor-pointer">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            {sidebar}
            <button
              type="button"
              onClick={() => setMobileFilterOpen(false)}
              className="mt-4 w-full py-3 rounded-md bg-primary-500 text-background-50 text-sm font-semibold cursor-pointer whitespace-nowrap"
            >
              查看 {filtered.length} 部结果
            </button>
          </div>
        </div>
      )}
    </div>
  );
}